import { fetchReviewQueue } from "api/admin/referrals";
import useAdminAccess from "hooks/useAdminAccess";
import { useState } from "react";
import { useQuery } from "react-query";
import Table from "components/ui/Table";
import ReferralActions, { Decision } from "./ReferralActions";
import {
  Button,
  Pages,
  UserLink,
  errorMessage,
  formatDate,
  formatLabel,
  queryOptions,
  useReferralKey,
} from "./shared";
export default function ReviewQueue() {
  const [page, setPage] = useState(1),
    [decision, setDecision] = useState<Decision>(),
    [message, setMessage] = useState("");
  const key = useReferralKey();
  const { can } = useAdminAccess();
  const query = useQuery(
    [...key, "review", page],
    () => fetchReviewQueue(page),
    queryOptions,
  );
  return (
    <div>
      <h2 className="text-lg font-medium">Review queue</h2>
      <p className="my-3 text-sm text-gray-500">
        Pending referrals with open fraud flags. Qualified referrals appear
        first. A match is a reason to investigate, not proof of fraud.
      </p>
      {message && (
        <p role="status" className="my-3 text-green-700">
          {message}
        </p>
      )}
      {decision && (
        <ReferralActions
          decision={decision}
          cancel={() => setDecision(undefined)}
          done={(text) => {
            setMessage(text);
            setDecision(undefined);
          }}
        />
      )}
      <div className="overflow-x-auto">
        <Table
          header={[
            "Referral",
            "Participants",
            "Qualification",
            "Open flags",
            "Actions",
          ]}
          isLoading={query.isLoading}
          errorMessage={query.isError ? errorMessage(query.error) : undefined}
          onRetry={() => void query.refetch()}
          emptyMessage="No referrals are waiting for review."
          data={(query.data?.results ?? []).map((row) => [
            `#${row.referralId}`,
            <span>
              Referrer: <UserLink id={row.referrerId} /> ({row.referrerMarket})
              <br />
              Referee: <UserLink id={row.refereeId} /> ({row.refereeMarket})
            </span>,
            row.qualifiedAt
              ? formatDate(row.qualifiedAt)
              : "Requirements incomplete",
            <ul className="min-w-[200px] space-y-3">
              {row.openFlags.map((flag) => (
                <li key={flag.id}>
                  {formatLabel(flag.signal)} match ·{" "}
                  <UserLink id={flag.matchedUserId} />
                  <br />
                  {formatLabel(flag.raisedStage)} · {formatDate(flag.raisedAt)}
                </li>
              ))}
            </ul>,
            can("referrals.reviews.approve") ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDecision({ id: row.referralId, action: "clear-hold" });
                    setMessage("");
                  }}
                >
                  Clear hold
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setDecision({ id: row.referralId, action: "reject" });
                    setMessage("");
                  }}
                >
                  Reject referral
                </Button>
              </div>
            ) : (
              "Read-only"
            ),
            undefined,
          ])}
        />
      </div>
      <Pages
        meta={query.data?.meta}
        busy={query.isFetching}
        change={(p) => {
          setPage(p);
          setDecision(undefined);
        }}
      />
    </div>
  );
}
